import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import Service from './Service'
import PhoneIcon from 'assets/phone-call.svg'
import WhatsappIcon from 'assets/whatsapp-simple-2.svg'
import MailIcon from 'assets/mail.svg'
import { graphql, useStaticQuery } from 'gatsby'
import useTranslations from 'hooks/useTranslations'
import Link from 'theme/atoms/Link'

export type Gq = {
  contactBox: {
    customerServiceImage: string
    contactInfo: {
      info: string
      type: 'phonenumber' | 'whatsapp' | 'email'
      title: string
      value: string
    }[]
  }
}

export default function ContactBox() {
  const gq: Gq = useStaticQuery(graphql`
    query appContactBox {
      contactBox {
        customerServiceImage
        contactInfo {
          info
          type
          title
          value
        }
      }
    }
  `)
  const t = useTranslations<'app-contact-box'>()

  const { contactBox } = gq

  return (
    <Wrapper className="ContactBox">
      <div className="title">{t('app_contact_box_headline')}</div>
      <p>
        {t('app_contact_box_description', {
          SERVICE_LINK: '_',
          __transformers: {
            SERVICE_LINK: (_, vars) => (
              <Link to={vars.SERVICE_LINK_url}>{vars.SERVICE_LINK_label}</Link>
            )
          }
        })}
      </p>
      <div className="contact">
        <div className="highlight-person">
          <img
            width={90}
            height={90}
            alt="person"
            src={contactBox.customerServiceImage}
          />
          <div className="info">
            <div className="quote">
              {t('app_contact_box_customer_service_info')}
            </div>
            <div className="person">
              {t('app_contact_box_customer_service_agent', {
                AGENT_NAME: '_',
                __transformers: {
                  AGENT_NAME: (_, vars) => {
                    return <span className="name">{vars.AGENT_NAME},</span>
                  }
                }
              })}
            </div>
          </div>
        </div>
        <div className="services">
          {contactBox.contactInfo.map((service, i) => {
            if (service.type === 'phonenumber')
              return (
                <Service
                  key={i}
                  title={service.title}
                  icon={<PhoneIcon />}
                  link={`tel:${service.value}`}
                  value={service.value}
                  subtitle={service.info}
                />
              )
            if (service.type === 'whatsapp')
              return (
                <Service
                  key={i}
                  title={service.title}
                  icon={<WhatsappIcon />}
                  iconScale={2}
                  link={`https://wa.me/${service.value.replace(/[^0-9]/g, '')}`}
                  value={service.value}
                  subtitle={service.info}
                />
              )

            return (
              <Service
                key={i}
                title={service.title}
                icon={<MailIcon />}
                link={`mailto:${service.value}`}
                value={service.value}
              />
            )
          })}
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding: ${theme.spacing('m')};
  background: ${theme.colors.shade.b7};

  > .title {
    ${theme.ty('rc-base', 'bold', 'rc-2xl')}
    margin-bottom: ${theme.spacing('xxs')};
  }

  > p {
    margin: 0;
    ${theme.ty('rc-s', null, 'rc-base')}
    margin-bottom: ${theme.spacing('m')};
    > span a {
      font-weight: bold;
      text-decoration: underline;
    }
  }

  > .contact {
    display: flex;
    gap: ${theme.spacing('m')};
    flex-wrap: wrap;

    @media (min-width: ${theme.ms.XL}px) {
      flex-wrap: nowrap;
    }

    > .highlight-person {
      display: none;
      @media (min-width: ${theme.ms.XL}px) {
        display: flex;
        gap: ${theme.spacing('m')};
        flex-basis: 60%;
        min-width: 300px;
        > img {
          float: left;
          display: block;
          background: silver;
          border-radius: 50%;
        }
        > .info {
          ${theme.ty('rc-base')}
          > .quote {
            margin-bottom: ${theme.spacing('ml')};
          }
          > .person span > .name {
            font-weight: bold;
          }
        }
      }
    }

    > .services {
      display: flex;
      flex-wrap: wrap;
      gap: ${theme.spacing('m')};
      > .Service {
        flex-basis: 40%;
        > .group {
          > svg {
            padding: 5px;
          }
        }
      }
    }
  }
`
